import { useState } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useSEO } from '../../utils/seo';

export default function BookStrategyCallPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useSEO({
    title: 'Book Free Strategy Call | Healthcare Marketing Consultation | MillionDollarMed',
    description: 'Schedule a free 30-minute strategy call with MillionDollarMed. Discover how HIPAA-compliant marketing automation can help you attract more qualified patients and grow your healthcare practice. No obligation consultation.',
    keywords: 'healthcare marketing consultation, free strategy call, medical practice growth, patient acquisition consultation, HIPAA compliant marketing strategy',
    canonical: `${siteUrl}/book-strategy-call`,
    schema: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Free Healthcare Marketing Strategy Call",
      "description": "Free 30-minute consultation to discuss HIPAA-compliant marketing strategies for healthcare practices",
      "provider": {
        "@type": "Organization",
        "name": "MillionDollarMed",
        "url": siteUrl
      },
      "areaServed": "US",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      }
    }
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    practiceName: '',
    specialty: '',
    currentChallenges: '',
    goals: '',
    preferredTime: '',
    website: '',
    monthlyPatients: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    // Validate required fields
    if (!formData.name || !formData.email || !formData.phone || !formData.practiceName || !formData.specialty) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    // Validate textarea length
    if (formData.currentChallenges.length > 500 || formData.goals.length > 500) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d62je8gv3sjav1bavkc0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          practiceName: '',
          specialty: '',
          currentChallenges: '',
          goals: '',
          preferredTime: '',
          website: '',
          monthlyPatients: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-purple-600 via-purple-700 to-violet-800">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <i className="ri-calendar-check-line text-xl"></i>
              <span className="font-semibold">Free 30-Minute Consultation</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Book Your Free Strategy Call
            </h1>
            <p className="text-xl md:text-2xl text-purple-100 leading-relaxed">
              Discover how HIPAA-compliant marketing automation can help you attract more qualified patients and grow your practice
            </p>
          </div>
        </div>
      </section>

      {/* What to Expect Section */}
      <section className="py-16 bg-gradient-to-br from-purple-50 to-violet-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">What to Expect on Your Call</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              This is a no-pressure consultation focused on understanding your practice and exploring growth opportunities
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6 max-w-6xl mx-auto">
            <div className="bg-white rounded-xl p-6 shadow-lg text-center">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-violet-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">1</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Practice Assessment</h3>
              <p className="text-gray-700 text-sm">
                We'll discuss your current patient acquisition challenges and marketing efforts
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg text-center">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-violet-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">2</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Growth Opportunities</h3>
              <p className="text-gray-700 text-sm">
                Identify untapped opportunities to attract your ideal patients
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg text-center">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-violet-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">3</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Automation Demo</h3>
              <p className="text-gray-700 text-sm">
                See how our platform can automate lead nurturing and patient engagement
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg text-center">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-violet-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">4</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Custom Strategy</h3>
              <p className="text-gray-700 text-sm">
                Receive a tailored growth plan specific to your practice and specialty
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-8 md:p-12">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Schedule Your Strategy Call
              </h2>
              <p className="text-lg text-gray-600">
                Fill out the form below and we'll reach out within 24 hours to schedule your consultation
              </p>
            </div>

            <form id="strategy-call-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              {/* Name and Email */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-bold text-gray-900 mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent text-sm"
                    placeholder="Dr. John Smith"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-bold text-gray-900 mb-2">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent text-sm"
                    placeholder="doctor@practice.com"
                  />
                </div>
              </div>

              {/* Phone and Practice Name */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-bold text-gray-900 mb-2">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent text-sm"
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div>
                  <label htmlFor="practiceName" className="block text-sm font-bold text-gray-900 mb-2">
                    Practice Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="practiceName"
                    name="practiceName"
                    value={formData.practiceName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent text-sm"
                    placeholder="Your Practice Name"
                  />
                </div>
              </div>

              {/* Specialty */}
              <div>
                <label htmlFor="specialty" className="block text-sm font-bold text-gray-900 mb-2">
                  Medical Specialty <span className="text-red-500">*</span>
                </label>
                <select
                  id="specialty"
                  name="specialty"
                  value={formData.specialty}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent text-sm"
                >
                  <option value="">Select your specialty</option>
                  <option value="obgyn-menopause">OB-GYN / Menopause Specialist</option>
                  <option value="preventive-medicine">Preventive / Functional / Integrative Medicine</option>
                  <option value="nurse-practitioner">Nurse Practitioner - Women's Health / Hormone Therapy</option>
                  <option value="physician-assistant">Physician Assistant - Hormone / Wellness</option>
                  <option value="cardiology">Cardiology with Prevention / Hormone Optimization</option>
                  <option value="endocrinology">Endocrinology / Metabolic Health</option>
                  <option value="urology">Urology / Men's Health</option>
                  <option value="functional-integrative">Functional / Integrative Medicine Clinic</option>
                  <option value="longevity-antiaging">Longevity / Anti-aging / Performance Medicine</option>
                  <option value="bhrt-hormone">BHRT / Hormone Therapy Center</option>
                  <option value="wellness-center">Wellness Center</option>
                  <option value="medical-spa">Medical Spa with Hormone Therapy</option>
                  <option value="primary-care">Primary Care / Prevention Focus</option>
                  <option value="clinic-owner">Clinic Owner / Medical Director</option>
                  <option value="other">Other Healthcare Specialty</option>
                </select>
              </div>

              {/* Website and Monthly Patients */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="website" className="block text-sm font-bold text-gray-900 mb-2">
                    Practice Website
                  </label>
                  <input
                    type="url"
                    id="website"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent text-sm"
                    placeholder="https://yourpractice.com"
                  />
                </div>

                <div>
                  <label htmlFor="monthlyPatients" className="block text-sm font-bold text-gray-900 mb-2">
                    New Patients Per Month
                  </label>
                  <select
                    id="monthlyPatients"
                    name="monthlyPatients"
                    value={formData.monthlyPatients}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent text-sm"
                  >
                    <option value="">Select range</option>
                    <option value="0-10">0-10 patients</option>
                    <option value="11-25">11-25 patients</option>
                    <option value="26-50">26-50 patients</option>
                    <option value="51-100">51-100 patients</option>
                    <option value="100+">100+ patients</option>
                  </select>
                </div>
              </div>

              {/* Current Challenges */}
              <div>
                <label htmlFor="currentChallenges" className="block text-sm font-bold text-gray-900 mb-2">
                  Current Marketing Challenges
                </label>
                <textarea
                  id="currentChallenges"
                  name="currentChallenges"
                  value={formData.currentChallenges}
                  onChange={handleChange}
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent text-sm resize-none"
                  placeholder="What are your biggest challenges with patient acquisition and marketing? (Max 500 characters)"
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">{formData.currentChallenges.length}/500 characters</p>
              </div>

              {/* Goals */}
              <div>
                <label htmlFor="goals" className="block text-sm font-bold text-gray-900 mb-2">
                  Practice Growth Goals
                </label>
                <textarea
                  id="goals"
                  name="goals"
                  value={formData.goals}
                  onChange={handleChange}
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent text-sm resize-none"
                  placeholder="What are your goals for the next 6-12 months? (Max 500 characters)"
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">{formData.goals.length}/500 characters</p>
              </div>

              {/* Preferred Time */}
              <div>
                <label htmlFor="preferredTime" className="block text-sm font-bold text-gray-900 mb-2">
                  Preferred Contact Time
                </label>
                <select
                  id="preferredTime"
                  name="preferredTime"
                  value={formData.preferredTime}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent text-sm"
                >
                  <option value="">Select preferred time</option>
                  <option value="morning">Morning (8am - 12pm)</option>
                  <option value="afternoon">Afternoon (12pm - 5pm)</option>
                  <option value="evening">Evening (5pm - 8pm)</option>
                  <option value="flexible">Flexible / Any Time</option>
                </select>
              </div>

              {/* HIPAA Notice */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <i className="ri-shield-check-line text-purple-600 text-xl flex-shrink-0 mt-1"></i>
                  <p className="text-sm text-gray-700">
                    <strong className="text-gray-900">HIPAA-Compliant Communication:</strong> Your information is securely stored and will only be used to schedule your consultation. We follow strict data protection protocols and will never share your information with third parties.
                  </p>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-gradient-to-r from-purple-600 to-violet-600 text-white rounded-lg font-bold text-lg hover:from-purple-700 hover:to-violet-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? 'Submitting...' : 'Book My Free Strategy Call'}
              </button>

              {/* Status Messages */}
              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <i className="ri-check-line text-green-600 text-2xl mb-2"></i>
                  <p className="text-green-800 font-semibold">Thank you! We'll contact you within 24 hours to schedule your strategy call.</p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                  <i className="ri-error-warning-line text-red-600 text-2xl mb-2"></i>
                  <p className="text-red-800 font-semibold">Please fill in all required fields and ensure text areas don't exceed 500 characters.</p>
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-br from-purple-50 to-violet-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Why Healthcare Providers Choose Us</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-violet-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-hospital-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Healthcare-Only Expertise</h3>
              <p className="text-gray-700">
                We exclusively serve medical practices and understand the unique challenges of healthcare marketing
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-violet-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-robot-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Proprietary Automation</h3>
              <p className="text-gray-700">
                Our platform automates lead nurturing, appointment booking, and patient engagement 24/7
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-violet-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">HIPAA-Compliant Systems</h3>
              <p className="text-gray-700">
                Every marketing system we build is designed with patient privacy and HIPAA compliance at its core
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
          </div>

          <div className="space-y-6">
            <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Is there any cost for the strategy call?</h3>
              <p className="text-gray-700">
                No, the strategy call is completely free with no obligation. We'll assess your practice, identify growth opportunities, and provide actionable insights whether you work with us or not.
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">How long is the consultation?</h3>
              <p className="text-gray-700">
                The call typically lasts 30-45 minutes. We'll spend time understanding your practice, discussing your goals, and showing you how our automation platform can help.
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Do you only work with certain medical specialties?</h3>
              <p className="text-gray-700">
                We specialize in preventive medicine, hormone therapy, longevity, functional medicine, and wellness-focused practices, but we work with all healthcare providers who want to grow ethically and compliantly.
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">How quickly can I expect results?</h3>
              <p className="text-gray-700">
                Most practices see increased lead flow within 30-60 days and significant patient acquisition improvements within 90 days. We'll discuss realistic timelines based on your specific situation during the call.
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Is my information kept confidential?</h3>
              <p className="text-gray-700">
                Absolutely. We follow strict HIPAA-compliant data handling protocols. Your information is securely stored and will never be shared with third parties.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
